// Pure client-side tax math mirroring the accounting server. The server
// re-computes tax from a line's `tax_rate_id` and never trusts a client tax
// value, so anything derived here is an estimate shown for feedback only.

/** Basis points: 100 = 1%. */
export function calcTaxCents(amountCents: number, rateBp: number): number {
  if (!Number.isFinite(amountCents) || !Number.isFinite(rateBp)) return 0;
  // Mirrors `sylviq-accounting` `domain::calc_tax` exactly:
  //   js_math_round((amount_cents * rate_bp) / 10000)
  //   js_math_round(x) = (x + 0.5).floor()  // half away from -infinity
  return Math.floor((amountCents * rateBp) / 10000 + 0.5);
}

interface TaxableLine {
  /** Line amount in minor units; the entered amount stays authoritative. */
  amountCents: number;
  /** Parent rate in basis points; undefined means no tax on this line. */
  rateBp?: number;
}

interface LineTotals {
  subtotalCents: number;
  /** Sum of each line's estimated tax, rounded per line like the server. */
  taxCents: number;
  totalCents: number;
}

/** Estimated document totals from already-parsed lines. Unknown/invalid
 * amounts contribute nothing rather than throwing, so a half-typed draft
 * never breaks the live preview. */
export function totalTaxableLines(lines: readonly TaxableLine[]): LineTotals {
  let subtotalCents = 0;
  let taxCents = 0;
  for (const line of lines) {
    const amount = Number.isSafeInteger(line.amountCents) ? line.amountCents : 0;
    subtotalCents += amount;
    if (line.rateBp !== undefined) taxCents += calcTaxCents(amount, line.rateBp);
  }
  return {
    subtotalCents,
    taxCents,
    totalCents: subtotalCents + taxCents,
  };
}
